import React, { useContext, useEffect } from "react";
import {
	Box,
	Fab,
	AddIcon,
	Menu,
	MenuItem,
	Grid,
	Tooltip,
} from "../../../common/MaterialComponent";
import { WidgetTypeEnum } from "../../../common/enum";
import { PreSubmissionConfigurationModel } from "../../../models/BatchMessage/BatchMessageDetailModel";
import "./../IVRFlowConfiguration/IVRFlowConfiguration.css";
import { IVRFlowWizardStepper } from "../IVRFlowWizardStepper/IVRFlowWizardStepper";
import {
	IVRFlowWizardContext,
	IVRContextType,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import Shared from "../../../common/shared";
import { IDropDown } from "../../../common/interfaces";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";
import IVRFlowConfigurationModel from "../../../models/RequestForHelpTemplates/IVRFlowConfigurationModel";

export interface IVRFlowWizardConfigurationProp {
	flowConfigurationDetail: PreSubmissionConfigurationModel;
	hasFlowConfigChanged: boolean;
	widgetIdToBeValidated: string;
	setHasFlowConfigChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const IVRFlowWizardConfiguration: React.FC<IVRFlowWizardConfigurationProp> = ({
	flowConfigurationDetail,
	hasFlowConfigChanged,
	widgetIdToBeValidated,
	setHasFlowConfigChanged,
}) => {
	const { state, dispatch } = useContext<IVRContextType>(IVRFlowWizardContext);

	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const widgetTypeOptions: IDropDown[] = Shared.convertEnumToList(
		WidgetTypeEnum
	);
	const strings = useGetComponentStringsContext().IVRFlowWizardConfiguration;

	useEffect(() => {
		if (state.isCreateNewWidget) {
			const element = document.getElementsByClassName("addWidgetBtnDiv")[0];
			if (element) {
				window.scrollTo({
					top: (element as HTMLElement).offsetTop,
					behavior: "smooth",
				});
				// reset isCreateWidget value
				dispatch({
					type: "HANDLE_CREATE_WIDGET",
					payload: {
						isCreateWidget: false,
					},
				});
			}
		}
	}, [dispatch, state.isCreateNewWidget]);

	useEffect(() => {
		dispatch({
			type: "INITIALIZE_WIZARD_STEPPER_DETAIL",
			payload: {
				flowConfigurationDetail: flowConfigurationDetail.ivrFlowJson
					? (flowConfigurationDetail.ivrFlowJson as IVRFlowConfigurationModel)
					: new IVRFlowConfigurationModel(),
				rowKey: flowConfigurationDetail.rowKey,
				partitionKey: flowConfigurationDetail.partitionKey,
				ivrFlowType: flowConfigurationDetail.ivrFlowType,
			},
		});
		// disabled eslint rule as useEffect needs to be called once on page load to initialize state
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flowConfigurationDetail.partitionKey]);

	useEffect(() => {
		if (state.hasFlowConfigChanged) {
			setHasFlowConfigChanged(true);
		}
	}, [setHasFlowConfigChanged, state.hasFlowConfigChanged]);

	useEffect(() => {
		if (!hasFlowConfigChanged) {
			dispatch({
				type: "RESET_HAS_FLOW_CONFIG_CHANGED",
				payload: {},
			});
		}
	}, [dispatch, hasFlowConfigChanged]);

	const addNewWidget = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		dispatch({
			type: "HANDLE_CREATE_WIDGET",
			payload: {
				isCreateWidget: false,
			},
		});
	};

	const handleWidgetTypeSelection = (
		event: React.MouseEvent<HTMLElement>,
		selectedWidget: IDropDown
	) => {
		dispatch({
			type: "ADD_NEW_WIDGET",
			payload: {
				widgetType: selectedWidget.type as WidgetTypeEnum,
				widgetTitle: selectedWidget.description,
			},
		});
		setAnchorEl(null);
	};

	const handleDeleteWidget = (widgetId: string) => {
		dispatch({
			type: "DELETE_WIDGET",
			payload: {
				id: widgetId,
			},
		});
	};

	const setActiveStepperId = (widgetId: string) => {
		dispatch({
			type: "SET_ACTIVE_STEPPER_ID",
			payload: {
				id: widgetId,
			},
		});
	};

	const getWidgetTypeOptions = () => {
		if (state.stepperContent.length > 0) {
			const logRequestStepIndex = state.stepperContent.findIndex(
				(c) => c.type === WidgetTypeEnum.LogRequestForHelp
			);
			return widgetTypeOptions.filter((w) =>
				logRequestStepIndex > -1
					? w.type !== WidgetTypeEnum.LogRequestForHelp &&
					  w.type !== WidgetTypeEnum.PickLanguage
					: w.type !== WidgetTypeEnum.PickLanguage
			);
		}
		return widgetTypeOptions;
	};

	return (
		<>
			<Box mt={1}>
				<Grid container spacing={1}>
					{state.stepperContent.length === 0 ? (
						<Grid item xs={12}>
							<Box textAlign="center">{strings.noWidgetConfiguredMessage}</Box>
						</Grid>
					) : (
						<Grid item xs={12}>
							<IVRFlowWizardStepper
								stepperContent={state.stepperContent}
								activeStepperId={state.activeStepperId}
								widgetIdToBeValidated={widgetIdToBeValidated}
								handleDeleteWidget={handleDeleteWidget}
								setActiveStepperId={setActiveStepperId}
							/>
						</Grid>
					)}
					<Grid item xs={12}>
						<Box mt={1} className="addWidgetBtnDiv">
							<Tooltip title={strings.addWidgetTooltip}>
								<span>
									<Fab
										size="medium"
										color="primary"
										onClick={addNewWidget}
										disabled={!flowConfigurationDetail.id}
									>
										<AddIcon className="addWidgetIcon" />
									</Fab>
								</span>
							</Tooltip>
						</Box>
					</Grid>
				</Grid>
				<Menu
					anchorEl={anchorEl}
					keepMounted
					open={Boolean(anchorEl)}
					getContentAnchorEl={null}
					anchorOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					onClose={() => {
						setAnchorEl(null);
					}}
				>
					{getWidgetTypeOptions().map((option, index) => (
						<MenuItem
							key={index}
							onClick={(event) => handleWidgetTypeSelection(event, option)}
						>
							{option.description}
						</MenuItem>
					))}
				</Menu>
			</Box>
		</>
	);
};
